import { ChevronRightRounded, HelpOutlineRounded } from '@mui/icons-material'
import { Box, TextField, Tooltip, Typography, Divider, Select, MenuItem } from '@mui/material'
import React, { useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import PreviewBox from './preview-box'
import AccordionResponseForm, { RESPONSE_TYPES } from './commitment-check-response-form'
import { useSelector } from 'react-redux'
import { SkillBiteEditSlice } from '../../../../../../reducers'

const MOCKED_LAST_SESSION_COMMITMENT = '<LAST SESSION COMMITMENT GOES HERE>'

const CommitmentCheckPageBlock = ({ index }) => {
  const { watch, control } = useFormContext()
  const watchBlock = watch(`blocks.${index}`)
  const blockDataFormIndex = `blocks.${index}.data`
  const commitmentBlocks = useSelector(SkillBiteEditSlice.selectors.selectSkillBiteCommitmentBlocks)
  const selectedSession = useSelector(SkillBiteEditSlice.selectors.selectSelectedSession)
  const canEditStructure = useSelector(SkillBiteEditSlice.selectors.selectCanEditStructure)
  const canEdit = useSelector(SkillBiteEditSlice.selectors.selectEditMode)

  const availableCommitmentBlocks = useMemo(() => {
    const previousSessionsCommitments = commitmentBlocks.filter((commitment) => commitment.session.id !== selectedSession.id && commitment.session.order < selectedSession.order)
    previousSessionsCommitments.sort((a, b) => b.session.order - a.session.order)
    return previousSessionsCommitments
  }, [commitmentBlocks, selectedSession])

  const hasSelectedCommitmentId = !!watchBlock?.data?.commitmentId

  return (
    <Box
      px={3}
      py={2}
      sx={{
        border: '2px solid #EEE',
        borderRadius: '10px'
      }}
    >
      <Box display='flex' flexDirection='row' alignItems='center' gap={1}>
        <Typography sx={{ fontSize: '16px', fontWeight: 600, lineHeight: '24px' }}>Commitment Check</Typography>
        <Tooltip title='Commitment Check description here...'>
          <HelpOutlineRounded fontSize='small' htmlColor='#BCBCBC' />
        </Tooltip>
      </Box>
      <Box mt={2}>
        <Typography>Link the expected commitment</Typography>
        <Controller
          name={`${blockDataFormIndex}.commitmentId`}
          control={control}
          render={({ field }) => (
            <Select
              variant='outlined'
              sx={{ width: '100%', marginBottom: '16px' }}
              {...field}
              disabled={(!canEditStructure && hasSelectedCommitmentId) || !canEdit}
            >
              {availableCommitmentBlocks.map((commitmentBlock) => (
                <MenuItem key={commitmentBlock.id} value={commitmentBlock.id}>
                  <Box display='flex' flexDirection='column'>
                    <Typography variant='caption'>{commitmentBlock.session.name} <ChevronRightRounded fontSize='inherit' /> {commitmentBlock.section.name}</Typography>
                    <Typography>{commitmentBlock.label}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          )}
        />
      </Box>
      {hasSelectedCommitmentId && (
        <Box>
          <Box mt={2}>
            <Typography>Type the intro message below</Typography>
            <Controller
              name={`${blockDataFormIndex}.labels.lastTimeCommitment`}
              control={control}
              render={({ field }) => (
                <TextField
                  sx={{ marginTop: '16px', marginBottom: '8px', width: '100%' }}
                  disabled={!canEdit}
                  label='Intro Message'
                  {...field}
                />
              )}
            />
          </Box>
          <PreviewBox>
            <Box display='flex' flexDirection='column' data-test='skill-bite-commitment-check-container'>
              <Typography>{watchBlock?.data?.labels?.lastTimeCommitment}</Typography>
              <Typography
                sx={{
                  fontWeight: 600,
                  marginTop: '16px',
                  marginBottom: '24px'
                }}
              >
                "{MOCKED_LAST_SESSION_COMMITMENT}"
              </Typography>
              <Typography>Were you able to complete that commitment?</Typography>
            </Box>
          </PreviewBox>
          <Divider sx={{ marginTop: '16px' }} />
          <Box display='flex' flexDirection='column' gap='16px' sx={{ marginTop: '16px' }}>
            <Typography sx={{ fontSize: '16px', fontWeight: 600, lineHeight: '24px' }}>Responses</Typography>
            <AccordionResponseForm
              accordionTitle='YES'
              control={control}
              responseType={RESPONSE_TYPES.YES}
              block={watchBlock}
              blockDataFormIndex={blockDataFormIndex}
            />
            <AccordionResponseForm
              accordionTitle='NO'
              control={control}
              responseType={RESPONSE_TYPES.NO}
              block={watchBlock}
              blockDataFormIndex={blockDataFormIndex}
            />
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default CommitmentCheckPageBlock
